import { ref } from "vue";
import { isAxiosError } from "axios";
import * as Sentry from "@sentry/vue";

import { getProjectNameExists } from "@/api/services/BuildingProject";
import debounce from "@/services/Utils/debounce";

/**
 * The one wording for a project name that is already taken (RTLCA-5603). Shared so the create/edit
 * wizard and the clone/restore dialogs say the same thing about the same rule — three copies of the
 * sentence is three chances for them to drift apart.
 */
export const PROJECT_NAME_TAKEN_MESSAGE = "Project Name already exists";

export type ProjectNameAvailabilityOptions = {
  /**
   * A name the caller already owns, read at check time. Asking the API whether your own name is
   * taken always says yes, so an existing project renaming itself back to what it is called must not
   * be accused of a collision.
   *
   * A function rather than a value because the exemption depends on live state (the wizard only
   * grants it once the project exists). Return null when nothing is exempt — which is the right
   * answer for the clone and restore dialogs: `X (Clone)` is a name the SYSTEM guessed, not one
   * anybody owns, so it is exactly the case this check exists to catch.
   */
  ignoreName?: () => string | null | undefined;
};

/**
 * "Does the tenant already have a project called this?", debounced, for any field that names a
 * project.
 *
 * Extracted from ProjectDetails.vue, which was the app's only caller, so the clone and restore
 * dialogs could ask the same question the wizard asks. Both of those seed a name that is very likely
 * to be taken already — `X (Clone)` on a second clone, `X (v3)` on a repeated restore — and the
 * backend accepts duplicates (RestoreProjectVersionCommand only rejects an empty name), so nothing
 * else was catching them.
 *
 * The name is sent VERBATIM, minus the empty-name short-circuit: the API compares with `_eq` and
 * neither trims nor case-folds, so "Test" and "test" are different names and the caller must pass
 * exactly the string it is going to submit.
 *
 * The check is ADVISORY and deliberately fails open — see the catch below.
 */
export const useProjectNameAvailability = (options: ProjectNameAvailabilityOptions = {}) => {
  /** Whether the last resolved check found the name taken. False until proven otherwise. */
  const nameExists = ref(false);

  /**
   * Minted synchronously by every check, whether it ends up querying or not, and compared again when
   * an answer lands. It is what makes the NEWEST intent win: without it, typing fast lets a slow
   * answer for an earlier name overwrite a newer one, and clearing the field leaves the already
   * scheduled check free to re-flag the empty field 300ms later.
   */
  let latestCheck = 0;

  /**
   * Cases where there is nothing worth asking the server.
   *
   * An empty name cannot collide, and the API answers the empty query with a 400 — the required-field
   * validation owns that case, and asking anyway only put an error in the console.
   */
  const shouldQuery = (name: string): boolean => {
    if (!name.trim()) return false;

    const ownName = options.ignoreName?.();
    if (ownName === null || ownName === undefined) return true;
    return ownName !== name;
  };

  const runCheck = async (name: string, token: number) => {
    try {
      // `=== true`, not truthiness: getProjectNameExists returns `{ status }` rather than throwing on
      // some non-200 paths, and that object is truthy — which would accuse the user of a duplicate
      // name because the server erred.
      const exists = (await getProjectNameExists(name)) === true;
      if (token !== latestCheck) return;
      nameExists.value = exists;
    } catch (error: unknown) {
      if (token !== latestCheck) return;

      // Fail OPEN. This is a warning, not a gate: the endpoint asserts the TENANT-wide CreateProject
      // permission while restore and clone only need admin on the project itself, so a project admin
      // without tenant create rights gets a 403 here. Blocking them would refuse an action the
      // backend accepts.
      nameExists.value = false;

      // That 403 is an expected role difference rather than a fault, and this runs on every settled
      // keystroke, so reporting it would be pure noise. Everything else is worth knowing about.
      if (!isAxiosError(error) || error.response?.status !== 403) Sentry.captureException(error);
    }
  };

  const scheduleCheck = debounce((name: string, token: number) => {
    // A newer check was started after this timer was set — by an emptied field, or by
    // checkImmediately. Its answer is the current one, so this request is stale before it is sent.
    if (token !== latestCheck) return;
    void runCheck(name, token);
  });

  /** Debounced: for `@update:model-value`, where every keystroke would otherwise be a request. */
  const check = (name: string) => {
    const token = ++latestCheck;
    if (!shouldQuery(name)) {
      nameExists.value = false;
      return;
    }
    scheduleCheck(name, token);
  };

  /**
   * The same check without the debounce, for `onMounted` on a dialog that opens with a name already
   * filled in. A user who never touches the field would otherwise never trigger a check at all, and
   * waiting 300ms to start leaves the confirm button enabled over a name we are about to reject.
   */
  const checkImmediately = (name: string) => {
    const token = ++latestCheck;
    if (!shouldQuery(name)) {
      nameExists.value = false;
      return;
    }
    void runCheck(name, token);
  };

  return {
    // state
    nameExists,

    // actions
    check,
    checkImmediately,
  };
};
